import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Activity, Heart, Brain, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface WhoopConnectionPromptProps {
  context: 'sleep' | 'exercise' | 'meditation' | 'nervous-system' | 'mood';
  isConnected?: boolean;
}

export default function WhoopConnectionPrompt({ context, isConnected = false }: WhoopConnectionPromptProps) {
  const navigate = useNavigate();

  const benefits = {
    sleep: {
      icon: Activity,
      title: "See how your sleep impacts recovery",
      benefits: [
        "REM & Deep Sleep stages correlated with emotional regulation",
        "Sleep disturbances linked to HRV patterns",
        "Optimal bedtime recommendations based on recovery needs"
      ]
    },
    exercise: {
      icon: Heart,
      title: "Optimise your movement with biometric insights",
      benefits: [
        "Strain vs Recovery balance for sustainable training",
        "Exercise intensity recommendations based on HRV",
        "Track how different workouts affect your nervous system"
      ]
    },
    meditation: {
      icon: Brain,
      title: "Measure meditation's impact on your body",
      benefits: [
        "HRV improvements from consistent practice",
        "Recovery score correlation with mindfulness",
        "See which practices calm your nervous system most"
      ]
    },
    'nervous-system': {
      icon: Zap,
      title: "Track nervous system regulation in real-time",
      benefits: [
        "Before/after HRV changes from regulation tools",
        "Identify which techniques work best for you",
        "See physiological proof of emotional shifts"
      ]
    },
    mood: {
      icon: TrendingUp,
      title: "Understand the body-mind connection",
      benefits: [
        "Mood patterns correlated with recovery data",
        "Identify physical triggers for emotional states",
        "Data-driven insights for emotional wellbeing"
      ]
    }
  };

  const contextBenefits = benefits[context];
  const IconComponent = contextBenefits.icon;

  return (
    <Card className="p-4 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-primary/20">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <IconComponent className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-foreground">{contextBenefits.title}</h4>
            <Badge variant="secondary" className="text-xs">WHOOP</Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            {isConnected 
              ? "View your detailed insights on the WHOOP dashboard"
              : "Connect your WHOOP to unlock deeper insights"}
          </p>
          <ul className="space-y-1.5 mb-4">
            {contextBenefits.benefits.map((benefit, index) => (
              <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
          <Button 
            size="sm" 
            onClick={() => navigate('/whoop')}
            variant={isConnected ? "default" : "outline"}
            className="w-full"
          >
            {isConnected ? "Go Deeper" : "Connect WHOOP & Go Deeper"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
